import type { AdminOrderData } from "../page";
import { STATUS_OPTIONS } from "../page";

interface Props {
  order: AdminOrderData;
}

export default function AdminOrderHeader({ order }: Props) {
  const st = STATUS_OPTIONS.find(s => s.value === order.status) ?? STATUS_OPTIONS[0];
  const isBankTransfer = order.paymentLabel === "التحويل البنكي" || order.paymentLabel?.includes("بنك") || order.paymentLabel?.includes("تحويل");

  const progressSteps = ["pending", "confirmed", "shipped", "delivered"];
  const currentStep = ["pending","confirmed","shipped","delivered"].indexOf(order.status);
  const isCancelled = order.status === "cancelled";
  const progressPercent = isCancelled ? 0 : Math.max(0, Math.round((currentStep / (progressSteps.length - 1)) * 100));

  return (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
      {/* Accent bar */}
      <div className={`h-1 w-full ${
        isCancelled ? "bg-red-400" :
        order.status === "delivered" ? "bg-teal-400" :
        order.status === "shipped" ? "bg-indigo-400" :
        order.status === "confirmed" ? "bg-green-400" : "bg-amber-400"
      }`}></div>

      <div className="p-6">
        {/* Top row */}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <p className="text-xs text-gray-400 mb-1">رقم الطلب</p>
            <h2 className="text-2xl font-black text-[#1a1a1a] tracking-wide">#{order.orderRef}</h2>
            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {new Date(order.createdAt).toLocaleDateString("ar-SA", {
                year: "numeric", month: "long", day: "numeric",
                hour: "2-digit", minute: "2-digit",
              })}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className={`flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full border ${st.bg} ${st.border} ${st.color}`}>
              <i className={`${st.icon} text-sm`}></i>
              {st.label}
            </span>
            {isBankTransfer && (
              <span className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border ${
                order.status === "pending"
                  ? "bg-orange-50 border-orange-300 text-orange-700"
                  : "bg-amber-50 border-amber-200 text-amber-700"
              }`}>
                <i className="ri-bank-line text-xs"></i>
                {order.status === "pending" ? "تحويل — بانتظار التأكيد" : "تحويل بنكي"}
              </span>
            )}
          </div>
        </div>

        {/* Progress */}
        {!isCancelled && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-400">تقدم الطلب</span>
              <span className="text-xs font-bold text-[#1a1a1a]">{progressPercent}%</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${
                  order.status === "delivered" ? "bg-teal-400" :
                  order.status === "shipped" ? "bg-indigo-400" :
                  order.status === "confirmed" ? "bg-green-400" : "bg-amber-400"
                }`}
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
            <div className="flex justify-between mt-3">
              {progressSteps.map((s, i) => {
                const done = currentStep >= i;
                const active = currentStep === i;
                const labels: Record<string, string> = { pending: "استلام", confirmed: "تأكيد", shipped: "شحن", delivered: "توصيل" };
                return (
                  <div key={s} className="flex flex-col items-center gap-1">
                    <div className={`w-3 h-3 rounded-full transition-all ${
                      done ? (active ? "bg-amber-400 ring-2 ring-amber-400/30" : "bg-green-400") : "bg-gray-200"
                    }`}></div>
                    <span className={`text-[10px] font-medium ${done ? (active ? "text-amber-600" : "text-green-600") : "text-gray-400"}`}>
                      {labels[s]}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {isCancelled && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <i className="ri-close-circle-line text-red-500 text-lg"></i>
            <p className="text-red-600 text-sm font-medium">تم إلغاء هذا الطلب</p>
          </div>
        )}
      </div>
    </div>
  );
}
